<template>
  <div v-if="isEditing" v-no-capture>
    <div class="edit-comment" :class="{ 'errored-field': errors && errors.length }">
      <ck-editor
        v-if="mentionableUsers.length > 0"
        theme="dark"
        ref="eventCommentCkEditor"
        :config="ckeditorConfig"
        :id="`eventCommentEditor-${event.id}-${uuid}`"
        :initial-data="event.comment"
        :initial-data-type="event.commentType"
        @save="saveComment(event, $event, $refs.submitBar.notifySubscribers)"
      />
      <div class="d-flex">
        <submit-buttons
          class="mt-3"
          ref="submitBar"
          submit-text="Save"
          :show-notify-subscribers="true"
          :is-loading="isSubmitting"
          @save="saveComment(event, $refs.eventCommentCkEditor.getData(), $event)"
          @cancel="isEditing = false"
        />
      </div>
    </div>
    <div class="field-error-message" v-if="errors && errors.length">
      {{ errors.join('. ') }}
    </div>
  </div>
  <div class="edit-checkin-container" v-else-if="isCheckinEditing" v-no-capture>
    <checkin-card
      :key="kr.id"
      :kr="kr"
      :org-id="kr.organizationId"
      :event-id="event.id"
      :comment="event.comment"
      :comment-type="event.commentType"
      @update-checkin="onCheckinUpdate"
      @close="onCheckinCancel"
    />
  </div>
  <div class="goal-event-comment-container pr-1 event-content" v-else>
    <component label="Show full comment" :is="expanded ? 'div' : 'height-limiter'">
      <div
        class="goal-event-comment"
        v-html="formatFreeText(event.organizationId, event.comment, event.commentType)"
        v-no-capture
      ></div>
    </component>
    <reactions-list class="pt-2" :event="event" @reacted="$emit('reacted', $event)" />
    <div class="comment-edit-menu d-block position-absolute">
      <div class="d-flex">
        <b-button
          variant="link"
          class="text-secondary p-0 text-small-1 mr-2"
          @click="$emit('reply')"
          v-if="!hideReply"
          ><span>Reply</span>
        </b-button>
        <b-button
          variant="link"
          class="text-secondary p-0 mr-2 text-small-1 mr-2"
          @click="editComment"
          v-if="userId === event.actorId"
          ><span>{{ event.comment || isLastCheckinEvent ? 'Edit' : 'Add a Comment' }}</span>
        </b-button>
        <b-button
          variant="link"
          class="text-secondary p-0 text-small-1"
          v-b-modal="'delete-confirm-modal-' + uuid"
          v-if="isDeleteAllowed()"
          ><span>Delete</span>
        </b-button>
      </div>
      <confirm-dialog
        :id="'delete-confirm-modal-' + uuid"
        @ok="deleteEvent"
        ok-title="Yes, Delete"
        ok-variant="danger"
        :hide-header="true"
      >
        <div v-if="event.type === GOAL_EVENT_TYPE.CHECKIN">
          <p>Are you sure you want to delete this checkin?</p>
          <p>Deleting the checkin will revert the KR progress to its previous value.</p>
        </div>
        <div v-else>
          <p>Are you sure you want to delete this comment?</p>
        </div>
      </confirm-dialog>
    </div>
  </div>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import CkEditor from '@/components/CkEditor'
import CheckinCard from '@/components/kr/CheckinCard'
import ConfirmDialog from '@/components/ConfirmDialog'
import HeightLimiter from '@/components/HeightLimiter'
import ReactionsList from '@/components/reactions/ReactionsList'
import SubmitButtons from '@/components/SubmitButtons'
import { MIME_TYPE } from '@/constants/common'
import { GOAL_EVENT_TYPE } from '@/constants/goal'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import formatFreeText from '@/utils/formatFreeText'
import pickError from '@/utils/pickError'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BButton, VBModal } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This is the view for goal events that contain a comment.
 * It shows comment, reactions list and provides capability to edit/delete comment
 */
export default {
  name: 'GoalEventWithComment',
  props: ['event', 'expanded', 'mentionableUsers', 'hideReply', 'kr'],
  mixins: [EventBusMixin, ComponentIdMixin],
  components: {
    ConfirmDialog,
    SubmitButtons,
    CkEditor,
    BButton,
    ReactionsList,
    HeightLimiter,
    CheckinCard
  },
  directives: {
    'b-modal': VBModal
  },
  data() {
    return {
      isEditing: false,
      isCheckinEditing: false,
      isSubmitting: false,
      errors: null,
      GOAL_EVENT_TYPE
    }
  },
  computed: {
    userId() {
      return R.path(['getters', 'user', 'id'], this.$store)
    },
    isLastCheckinEvent() {
      return (
        this.event &&
        this.event.type === GOAL_EVENT_TYPE.CHECKIN &&
        this.kr &&
        this.kr.lastCheckinEvent &&
        this.kr.lastCheckinEvent.id === this.event.id
      )
    },
    ckeditorConfig() {
      return getCKEditorConfig({
        height: 200,
        startupFocus: true,
        users: this.mentionableUsers,
        orgId: this.event.organizationId
      })
    }
  },
  methods: {
    ...mapActions(['deleteGoalEvent', 'updateGoalEvent']),
    formatFreeText,
    editComment() {
      this.errors = null
      if (this.isLastCheckinEvent) {
        this.isCheckinEditing = true
      } else {
        this.isEditing = true
      }
    },
    updateComment(comment, event, notifySubscribers) {
      const payload = {
        comment: comment,
        commentType: MIME_TYPE.HTML,
        goalId: event.goalId,
        orgId: event.organizationId,
        eventId: event.id,
        krId: event.keyResultId,
        notifySubscribers
      }
      return this.updateGoalEvent(payload)
    },
    isDeleteAllowed() {
      return (
        this.userId === this.event.actorId &&
        (this.event.type === GOAL_EVENT_TYPE.COMMENT || this.isLastCheckinEvent)
      )
    },
    onCommentUpdate(event, patchResult) {
      this.$emit('comment-updated', patchResult.comment)
      this.isEditing = false
    },
    onCheckinUpdate(event) {
      this.$emit('checkin-updated', event)
      this.isCheckinEditing = false
    },
    onCheckinCancel() {
      this.isCheckinEditing = false
    },
    async deleteEvent() {
      const eventType = this.event.type == GOAL_EVENT_TYPE.CHECKIN ? 'Checkin' : 'Comment'
      try {
        const { goalId, organizationId: orgId, id: eventId, keyResultId: krId } = this.event
        const patchResult = await this.deleteGoalEvent({ goalId, orgId, eventId, krId })
        if (patchResult.status < 400) {
          this.$emit('deleted')
        }
        showToast(`${eventType} has been deleted successfully`)
      } catch (e) {
        showAPIError(e)
      }
    },
    async saveComment(event, comment, notifySubscribers) {
      try {
        this.isSubmitting = true
        const patchResult = await this.updateComment(comment, event, notifySubscribers)
        this.eventBusEmit('ADD_TOAST', { message: 'Comment has been edited successfully' })
        this.onCommentUpdate(event, patchResult)
        this.errors = null
      } catch (e) {
        const error = R.pathOr([pickError(e)], ['response', 'data', 'detail', 'comment'], e)
        this.errors = error
      }
      this.isSubmitting = false
    }
  },
  mounted() {}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/errors';
@import '~@/styles/_styleConfigurations.scss';

.edit-comment {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: $quinary;
  border: 1px solid $borderPrimary;
  border-radius: 14px;
  padding: 0 16px 16px 16px;
  min-height: 314px;
  margin: 8px 0 16px 0;

  &.errored-field {
    border: 1px solid $error;
  }
}
.comment-edit-menu {
  opacity: 0;
  transition: opacity ease-in 100ms;
  margin-top: 2px;
}
.edit-checkin-container {
  border: 1px solid $sky;
  padding: 16px 40px;
  border-radius: 10px;
  background-color: $selectionColor;
}

.goal-event-comment-container {
  word-break: break-word;
  position: initial;
  padding-bottom: 29px;

  &:hover {
    position: relative;

    .comment-edit-menu {
      opacity: 1;
    }
  }

  /deep/ h3 {
    font-size: 1.3125rem;
  }
}
@include media-breakpoint-down(md) {
  .goal-event-comment-container {
    max-width: 100%;
  }
  .goal-event .comment-edit-menu {
    opacity: 1;
  }
  button.text-secondary {
    text-align: left;

    span {
      text-decoration: underline;
    }
  }
}
</style>
